import type {
  AccountProjection,
  CashflowPlan,
  CombinedCashflow,
  EventView,
  FxUnavailable,
  Minimum,
  NamedSeries,
  PlanSliceRow,
  Point,
  RegisterRow,
  EnvelopeProjection,
} from '@/api/projections';

export type ProjectionScope =
  | { kind: 'account'; id: number }
  | { kind: 'envelope'; id: number }
  | { kind: 'combined'; accountIds?: number[] }
  | { kind: 'virtualAccount'; virtualAccountId: number; envelopeId?: number };

/**
 * `categorical` strokes the entity's data colour's `line` via `categoricalFor(categoricalId)` —
 * by account id (per-account lines) or envelope id (per-envelope plan series), so an entity keeps its
 * colour when the list changes (TMMAPP-0037).
 */
export type ChartColor = 'brand' | 'text2' | 'categorical';

/** A drawn point: a projection `Point`, or the unallocated line whose value may be unserved. */
export interface ChartPoint {
  date: string;
  balanceMinor: number | null;
  unallocatedMinor: number | null;
}

export interface ChartSeries {
  id: string;
  nameKey: 'legendBalance' | 'legendUnallocated' | 'legendCombined' | 'named';
  name: string;
  points: ChartPoint[];
  color: ChartColor;
  categoricalId?: number;
  dashed?: boolean;
  faint?: boolean;
}

export interface DrillDown {
  from: string;
  to: string;
  highlight?: { date: string; sliceRef?: string; seriesId?: number };
}

export interface ProjectionView {
  from: string;
  to: string;
  /** The first day tmm-api served (TMMAPI-0188 U3): later than `from` asked means "From today". */
  windowFrom: string;
  chartSeries: ChartSeries[];
  events: EventView[];
  register: RegisterRow[];
  slices: PlanSliceRow[];
  fxUnavailable: FxUnavailable[];
  endMinor: number | null;
  endUnallocatedMinor: number | null;
  minimum: Minimum | null;
  trend: number;
  named: NamedSeries[];
  /** The combined view's accounts counted from 0 (TMMAPI-0160 U2); empty for every other scope. */
  openingPendingAccountIds: number[];
}

export function scopeKey(scope: ProjectionScope): string {
  switch (scope.kind) {
    case 'account':
      return `account:${scope.id}`;
    case 'envelope':
      return `envelope:${scope.id}`;
    case 'combined':
      return `combined:${(scope.accountIds ?? []).join(',')}`;
    case 'virtualAccount':
      return `virtualAccount:${scope.virtualAccountId}:${scope.envelopeId ?? ''}`;
  }
}

function pointsToBalanceSeries(id: string, points: Point[], name: string): ChartSeries {
  return {
    id,
    nameKey: 'named',
    name,
    points,
    color: 'brand',
  };
}

export function viewFromAccount(row: AccountProjection, accountName = ''): ProjectionView {
  const unallocated: ChartSeries = {
    id: 'unallocated',
    nameKey: 'legendUnallocated',
    name: '',
    points: row.series.map((p) => ({
      date: p.date,
      balanceMinor: p.unallocatedMinor,
      unallocatedMinor: p.unallocatedMinor,
    })),
    color: 'text2',
    dashed: true,
  };
  return {
    from: row.from,
    to: row.to,
    windowFrom: row.windowFrom,
    chartSeries: [
      {
        id: 'balance',
        nameKey: 'legendBalance',
        name: accountName,
        points: row.series,
        color: 'brand',
      },
      unallocated,
    ],
    events: row.events,
    register: row.register,
    slices: [],
    fxUnavailable: row.fxUnavailable,
    endMinor: row.endMinor,
    endUnallocatedMinor: row.endUnallocatedMinor,
    minimum: row.minimum,
    trend: row.trend,
    named: [],
    openingPendingAccountIds: [],
  };
}

export function viewFromEnvelope(row: EnvelopeProjection, envelopeName = ''): ProjectionView {
  return {
    from: row.from,
    to: row.to,
    windowFrom: row.windowFrom,
    chartSeries: [pointsToBalanceSeries('envelope', row.series, envelopeName)],
    events: row.events,
    register: row.register,
    slices: [],
    fxUnavailable: row.fxUnavailable,
    endMinor: row.endMinor,
    endUnallocatedMinor: null,
    minimum: row.minimum,
    trend: row.trend,
    named: [],
    openingPendingAccountIds: [],
  };
}

export function viewFromCombined(row: CombinedCashflow): ProjectionView {
  const combined: ChartSeries = {
    id: 'combined',
    nameKey: 'legendCombined',
    name: row.combined.name,
    points: row.combined.points,
    color: 'brand',
  };
  const accounts: ChartSeries[] = row.accounts.map((account) => ({
    id: `account-${account.accountId}`,
    nameKey: 'named' as const,
    name: account.accountName,
    points: account.series,
    color: 'categorical' as const,
    categoricalId: account.accountId,
    faint: true,
  }));
  return {
    from: row.from,
    to: row.to,
    windowFrom: row.windowFrom,
    chartSeries: [combined, ...accounts],
    events: row.events,
    register: row.register,
    slices: [],
    fxUnavailable: row.fxUnavailable,
    endMinor: row.combined.endMinor,
    endUnallocatedMinor: null,
    minimum: row.combined.minimum,
    trend: row.combined.trend,
    named: [row.combined],
    openingPendingAccountIds: row.openingPendingAccountIds,
  };
}

export function viewFromPlan(row: CashflowPlan): ProjectionView {
  const chartSeries: ChartSeries[] = row.series.map((s, index) => ({
    id: `series-${s.envelopeId ?? s.accountId ?? index}`,
    nameKey: 'named',
    name: s.name,
    points: s.points,
    // The scope's own series is the brand line; another envelope's series is its data colour.
    ...(index === 0
      ? { color: 'brand' as const }
      : s.envelopeId !== null
        ? { color: 'categorical' as const, categoricalId: s.envelopeId }
        : { color: 'text2' as const }),
  }));
  const first = row.series[0];
  return {
    from: row.from,
    to: row.to,
    windowFrom: row.windowFrom,
    chartSeries,
    events: row.events,
    register: row.register,
    slices: row.slices,
    fxUnavailable: row.fxUnavailable,
    endMinor: first?.endMinor ?? null,
    endUnallocatedMinor: null,
    minimum: first?.minimum ?? null,
    trend: first?.trend ?? 0,
    named: row.series,
    openingPendingAccountIds: [],
  };
}
